import type { MapRef } from 'react-map-gl/mapbox';

import { getZoomForLocationType } from '../search/utils';
import type { MapLocation } from './types';

export const LOCATION_MOVE_EVENT_DATA = { odSkipViewportSearch: true } as const;

type SearchMap = Pick<MapRef, 'isMoving' | 'getBounds'> & {
  flyTo: (...args: Parameters<MapRef['flyTo']>) => void;
  jumpTo: (...args: Parameters<MapRef['jumpTo']>) => void;
  stop: () => void;
  once: (event: 'moveend', listener: () => void) => void;
  off: (event: 'moveend', listener: () => void) => void;
};

export const moveToSearchLocation = ({
  map,
  location,
  onComplete,
}: {
  map: SearchMap;
  location: MapLocation & { coordinates: [number, number] };
  onComplete: () => void;
}): (() => void) => {
  // Finish an earlier animation before subscribing to this selection's moveend.
  map.stop();
  const camera = { center: location.coordinates, zoom: getZoomForLocationType(location.zoneType) };
  // A distant search is a new destination, not a tour across intervening cities.
  if (!map.getBounds()?.contains(location.coordinates)) {
    map.jumpTo(camera, LOCATION_MOVE_EVENT_DATA);
    onComplete();
    return () => {};
  }
  let pending = true;
  const cancel = () => {
    if (!pending) {
      return;
    }
    pending = false;
    map.off('moveend', complete);
    map.stop();
  };
  const complete = () => {
    if (!pending) {
      return;
    }
    pending = false;
    map.off('moveend', complete);
    onComplete();
  };
  map.once('moveend', complete);
  map.flyTo({ ...camera, duration: 400 }, LOCATION_MOVE_EVENT_DATA);
  // A repeated selection or reduced-motion preference can finish synchronously.
  if (!map.isMoving()) {
    complete();
  }
  return cancel;
};
